import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Table from 'components/Table'
import Pagination from 'components/Pagination'

import PagedSearch from 'components/PagedSearch'
import AlertNotification from 'pages/AlertNotificationConfigurations/AlertNotification'

import useSearchParams from 'hooks/useSearchParams'
import useCriticalAndMajorAlerts from 'hooks/useCriticalAndMajorAlerts'

import { screenSize, LoadingSinglePanePage, PageTitle, SinglePanePage } from 'styles/shared'

const ASC_SORT = 'asc'
const DESC_SORT = 'desc'

const AlertNotificationConfigurations = () => {
  const queryFromUrl = useSearchParams('q')
  const pageFromUrl = parseInt(useSearchParams('page')) || 1
  const sortAttributeFromUrl = useSearchParams('sortAttribute') || 'severity'
  const sortDirectionFromUrl = useSearchParams('sortDirection') || ASC_SORT

  const {
    criticalAndMajorAlerts,
    areCriticalAndMajorAlertsLoaded,
    criticalAndMajorAlertsMetaData
  } = useCriticalAndMajorAlerts({ page: pageFromUrl, sortAttribute: sortAttributeFromUrl, sortDirection: sortDirectionFromUrl, query: queryFromUrl })

  const navigate = useNavigate()

  const constructUrl = ({ page = pageFromUrl, sortAttribute = sortAttributeFromUrl, sortDirection = sortDirectionFromUrl, query = queryFromUrl }) => (
    `/admin/alert_notification_configurations?page=${page}&sortAttribute=${sortAttribute}&sortDirection=${sortDirection}${query ? `&q=${query}` : ''}`
  )

  const handlePageChange = (p) => {
    navigate(constructUrl({ page: p }))
  }

  const navigateToSortedList = (newAttribute) => {
    let newSortDirection

    if (sortAttributeFromUrl === newAttribute) {
      newSortDirection = sortDirectionFromUrl === ASC_SORT ? DESC_SORT : ASC_SORT
    } else {
      newSortDirection = ASC_SORT
    }

    navigate(constructUrl({ sortAttribute: newAttribute, sortDirection: newSortDirection }))
  }

  const sortDirectionForAttribute = (attribute) => {
    return (sortAttributeFromUrl === attribute) ? (sortDirectionFromUrl === ASC_SORT ? DESC_SORT : ASC_SORT) : null
  }

  if (!(areCriticalAndMajorAlertsLoaded)) {
    return <LoadingSinglePanePage />
  }

  return (
    <SinglePanePage>
      <PageTitle>Alert Notification Configurations</PageTitle>

      <S.NotificationSearchContainer>
        <PagedSearch query={queryFromUrl} onSearch={(newQuery) => { navigate(constructUrl({ query: newQuery })) }} />
      </S.NotificationSearchContainer>
      {criticalAndMajorAlertsMetaData?.filteredItemCount > 20 &&
      <S.TopPaginationContainer>
        <Pagination pageCount={criticalAndMajorAlertsMetaData.pageCount} onPageChange={handlePageChange} page={criticalAndMajorAlertsMetaData.pageNumber} />
      </S.TopPaginationContainer>
      }

      <Table
            items={criticalAndMajorAlerts}
            noDataMessage={{
              title: 'No Notifications recieved yet',
              description: ''
            }}
      >
        <S.Header>
          <Table.HeaderCell title="Notification" />
          <Table.HeaderCell title="Code" sortable onSort={() => { navigateToSortedList('code') }} sortDirection={sortDirectionForAttribute('code')} />
          <Table.HeaderCell title="Severity" sortable onSort={() => { navigateToSortedList('severity') }} sortDirection={sortDirectionForAttribute('severity')} />
          <Table.HeaderCell title="Description" />
          <Table.HeaderCell title="Problems" />
        </S.Header>
        {criticalAndMajorAlerts?.map((criticalAndMajorAlert) => (
          <S.Row key={criticalAndMajorAlert.code}>
            <AlertNotification criticalAndMajorAlert={criticalAndMajorAlert} />
          </S.Row>))
          }
      </Table>
      {criticalAndMajorAlertsMetaData?.filteredItemCount > 20 &&
      <S.BottomPaginationContainer>
        <Pagination pageCount={criticalAndMajorAlertsMetaData.pageCount} onPageChange={handlePageChange} page={criticalAndMajorAlertsMetaData.pageNumber} />
      </S.BottomPaginationContainer>
      }
    </SinglePanePage>
  )
}

export default AlertNotificationConfigurations

const S = {
  Header: styled(Table.Header)`
    display: grid;
    grid-template-columns: 12% 9% 10% 20% auto;

    @media ${screenSize.medium} {
      grid-template-columns:  15% 11% 13% 20% auto;
    }
    @media ${screenSize.small} {
      grid-template-columns: 36% 34% 33%;
      overflow: hidden;
      & > :nth-child(4) {
        visibility: hidden;
      }
      & > :nth-child(5) {
        visibility: hidden;
      }
    }
    `,
  Row: styled(Table.Row)`
    display: grid;
    grid-template-columns: 12% 9% 10% 20% auto;
    align-items: center;
    min-height: 50px;
    padding-top: 10px;
    padding-bottom: 10px;

    @media ${screenSize.medium} {
      grid-template-columns:  15% 11% 13% 20% auto;
    }

    @media ${screenSize.small} {
      grid-template-columns: 36% 34% 33%;
      overflow: hidden;
      & > :nth-child(4) {
        visibility: hidden;
      }
      & > :nth-child(5) {
        visibility: hidden;
      }
    }
    `,
  BottomPaginationContainer: styled.div`
    padding-top: 16px;
    padding-bottom: 40px;
  `,
  TopPaginationContainer: styled.div`
    padding-top: 16px;
    padding-bottom: 50px;
  `,
  NotificationSearchContainer: styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 15px;
  `
}
